import React, { useEffect, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCloudArrowUp,
  faKey,
  faPlay,
  faRotate,
  faTrashCan,
} from "@fortawesome/free-solid-svg-icons";
import resourcesService from "../services/Resources";
import { EnvironmentsData } from "../models/ResourcesTypes";
import "../styles/HeadBar.style.css";
import "../styles/Reprocess.style.css";

interface ReprocessProps {
  environments: EnvironmentsData;
  logs: string[];
}

const PROCESS_TYPES = [
  { value: "single", label: "single" },
  { value: "alarm", label: "alarm" },
  { value: "manual", label: "manual" },
  { value: "all", label: "all" },
];

const Reprocess: React.FC<ReprocessProps> = ({ environments, logs }) => {
  const [techno, setTechno] = useState<string>("orion");
  const [type, setType] = useState<string>("single");
  const [monthsAgo, setMonthsAgo] = useState<number>(1);
  const [ctrlIds, setCtrlIds] = useState<string>("");
  const [legacy, setLegacy] = useState<boolean>(false);
  const [dry, setDry] = useState<boolean>(false);
  const [location, setLocation] = useState<string>("");

  const [availableTechnos, setAvailableTechnos] = useState<string[]>([]);
  const [isRunning, setIsRunning] = useState<boolean>(false);
  const [isSyncing, setIsSyncing] = useState<boolean>(false);
  const [processLogs, setProcessLogs] = useState<string[]>([]);

  const [isSettingCreds, setIsSettingCreds] = useState<boolean>(false);
  const [credValues, setCredValues] = useState<Record<string, string>>({});

  const loggerRef = useRef<HTMLDivElement>(null);
  const lastLogIndexRef = useRef<number>(0);
  const isExecutingRef = useRef<boolean>(false);

  useEffect(() => {
    if (isSettingCreds) {
      const initialValues: Record<string, string> = {};
      if (environments) {
        Object.keys(environments)
          .filter((env) => !env.includes("gitlab"))
          .forEach((env) => {
            initialValues[env] = localStorage.getItem(env) || "";
          });
      }
      setCredValues(initialValues);
    }
  }, [environments, isSettingCreds]);

  const handleCredChange = (env: string, value: string) => {
    setCredValues((prev) => ({ ...prev, [env]: value }));
  };

  const handleSaveCreds = () => {
    if (credValues) {
      Object.entries(credValues).forEach(([env, value]) => {
        localStorage.setItem(env, value);
      });
      setIsSettingCreds(false);
    }
  };

  useEffect(() => {
    resourcesService.getPipelines().then((res) => {
      const list = (res && res.pipelines) || [];
      if (list.length > 0) {
        setAvailableTechnos(["all", ...list.sort()]);
      } else {
        setAvailableTechnos(["all", "orion"]);
      }
    });
  }, []);

  useEffect(() => {
    if (logs.length > lastLogIndexRef.current) {
      const newLogs = logs.slice(lastLogIndexRef.current);
      lastLogIndexRef.current = logs.length;
      if (isRunning || isSyncing) {
        const lines: string[] = [];
        newLogs.forEach((log) => {
          if (log) {
            log.split("\n").forEach((l) => {
              if (l.trim().length > 0) lines.push(l);
            });
          }
        });
        if (lines.length > 0) {
          setProcessLogs((prev) => [...prev, ...lines]);
        }
      }
    }
  }, [logs, isRunning, isSyncing]);

  useEffect(() => {
    if (loggerRef.current) {
      loggerRef.current.scrollTop = loggerRef.current.scrollHeight;
    }
  }, [processLogs]);

  const handleCopyDlkPush = async () => {
    if (isExecutingRef.current || isRunning || isSyncing) return;
    isExecutingRef.current = true;
    setIsSyncing(true);
    const initialLogIndex = logs.length;
    lastLogIndexRef.current = initialLogIndex;
    setProcessLogs((prev) => [
      ...prev,
      `[${new Date().toLocaleTimeString()}] Starting copy_dlk and bucket push (${dry ? "DRY RUN" : "REAL SYNC"})...`,
    ]);

    try {
      const response = await resourcesService.copyDlkAndPush(environments, {
        dry,
      });
      if (response.output && lastLogIndexRef.current <= initialLogIndex) {
        const lines = response.output
          .split("\n")
          .filter((l) => l.trim().length > 0);
        setProcessLogs((prev) => [...prev, ...lines]);
      }
      setProcessLogs((prev) => [
        ...prev,
        `[${new Date().toLocaleTimeString()}] ✓ Copy DLK and bucket push completed successfully.`,
      ]);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      const lines = msg.split("\n").filter((l) => l.trim().length > 0);
      setProcessLogs((prev) => [
        ...prev,
        `[${new Date().toLocaleTimeString()}] ✗ Error occurred:`,
        ...(lastLogIndexRef.current <= initialLogIndex ? lines : []),
      ]);
    } finally {
      isExecutingRef.current = false;
      setIsSyncing(false);
    }
  };

  const handleRun = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isExecutingRef.current || isRunning || isSyncing) return;
    const targetTechno = type === "manual" ? location.trim() : techno;
    if (!targetTechno) return;

    isExecutingRef.current = true;
    setIsRunning(true);
    const initialLogIndex = logs.length;
    lastLogIndexRef.current = initialLogIndex;
    setProcessLogs([
      `[${new Date().toLocaleTimeString()}] Starting reprocess for ${targetTechno} (${type})...`,
    ]);

    try {
      const response = await resourcesService.reprocess(
        {
          techno: targetTechno,
          type,
          months_ago: legacy ? undefined : monthsAgo,
          ctrl_ids: legacy || !ctrlIds.trim() ? undefined : ctrlIds.trim(),
          legacy,
          dry,
        },
        environments,
      );

      if (response.output && lastLogIndexRef.current <= initialLogIndex) {
        const lines = response.output
          .split("\n")
          .filter((l) => l.trim().length > 0);
        setProcessLogs((prev) => [...prev, ...lines]);
      }
      setProcessLogs((prev) => [
        ...prev,
        `[${new Date().toLocaleTimeString()}] ✓ Finished successfully.`,
      ]);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      const lines = msg.split("\n").filter((l) => l.trim().length > 0);
      setProcessLogs((prev) => [
        ...prev,
        `[${new Date().toLocaleTimeString()}] ✗ Error occurred:`,
        ...(lastLogIndexRef.current <= initialLogIndex ? lines : []),
      ]);
    } finally {
      isExecutingRef.current = false;
      setIsRunning(false);
    }
  };

  const clearLogs = () => {
    setProcessLogs([]);
  };

  return (
    <div className="reprocess-container">
      {isSettingCreds && environments && (
        <div className="credentials-container">
          <div className="credentials-card">
            {Object.keys(environments)
              .filter((env) => !env.includes("gitlab"))
              .map((env) => (
                <div key={env} className="env-field">
                  <h2>{env}</h2>
                  <textarea
                    name={env}
                    value={credValues[env] || ""}
                    onChange={(e) => handleCredChange(env, e.target.value)}
                  />
                </div>
              ))}
            <div className="credentials-controls">
              <button onClick={handleSaveCreds}>confirm</button>
              <button
                className="secondary"
                onClick={() => setIsSettingCreds(false)}
              >
                cancel
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="split">
        <div className="reprocess-panel">
          <div className="reprocess-title-bar">
            <h2>Reprocess</h2>
            <div
              className="icon"
              onClick={() => setIsSettingCreds(true)}
              title="Credentials"
            >
              <FontAwesomeIcon icon={faKey} />
            </div>
          </div>
          <form className="reprocess-form-simple" onSubmit={handleRun}>
            {type !== "manual" && (
              <div className="field">
                <label>Technology</label>
                <select
                  value={techno}
                  onChange={(e) => setTechno(e.target.value)}
                  disabled={isRunning}
                >
                  {availableTechnos.map((tech) => (
                    <option key={tech} value={tech}>
                      {tech}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div className="field">
              <label>Type</label>
              <select
                value={type}
                onChange={(e) => setType(e.target.value)}
                disabled={isRunning}
              >
                {PROCESS_TYPES.map((t) => (
                  <option key={t.value} value={t.value}>
                    {t.label}
                  </option>
                ))}
              </select>
            </div>

            {!legacy && (
              <div className="field">
                <label>Months Ago</label>
                <input
                  type="number"
                  min="0"
                  max="48"
                  value={monthsAgo}
                  onChange={(e) =>
                    setMonthsAgo(parseInt(e.target.value, 10) || 0)
                  }
                  disabled={isRunning}
                  required={!legacy}
                />
              </div>
            )}

            {!legacy && (
              <div className="field">
                <label>Controller IDs (comma-separated, optional)</label>
                <input
                  type="text"
                  placeholder="e.g. 1001, 1002"
                  value={ctrlIds}
                  onChange={(e) => setCtrlIds(e.target.value)}
                  disabled={isRunning}
                />
              </div>
            )}

            {type === "manual" && (
              <div className="field">
                <label>Location ID</label>
                <input
                  type="text"
                  placeholder="e.g. 2712801"
                  value={location}
                  onChange={(e) => setLocation(e.target.value)}
                  disabled={isRunning}
                  required
                />
              </div>
            )}

            <div className="checkboxes">
              <label>
                <input
                  type="checkbox"
                  checked={dry}
                  onChange={(e) => setDry(e.target.checked)}
                  disabled={isRunning}
                />
                <span>Dry run (--dry)</span>
              </label>

              <label>
                <input
                  type="checkbox"
                  checked={legacy}
                  onChange={(e) => setLegacy(e.target.checked)}
                  disabled={isRunning}
                />
                <span>Legacy mode (--legacy)</span>
              </label>
            </div>

            <div className="actions">
              <button
                type="submit"
                className="btn-run"
                disabled={isRunning || isSyncing}
              >
                <FontAwesomeIcon
                  icon={isRunning ? faRotate : faPlay}
                  spin={isRunning}
                />
                {isRunning ? "Running..." : "Run Reprocess"}
              </button>
              <button
                type="button"
                className="btn-clear"
                onClick={clearLogs}
                disabled={isRunning || isSyncing}
              >
                <FontAwesomeIcon icon={faTrashCan} />
                Clear
              </button>
            </div>

            <div className="secondary-actions">
              <button
                type="button"
                className="btn-sync"
                onClick={handleCopyDlkPush}
                disabled={isRunning || isSyncing}
              >
                <FontAwesomeIcon
                  icon={isSyncing ? faRotate : faCloudArrowUp}
                  spin={isSyncing}
                />
                {isSyncing ? "Syncing..." : "Copy DLK & Bucket Push"}
              </button>
            </div>
          </form>
        </div>

        <div className="logger" ref={loggerRef}>
          {processLogs.map((log: string, idx: number) => (
            <span key={idx}>{log}</span>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Reprocess;
